import { useState } from 'react';
import { Autocomplete, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { AdminPhoneModelItem, ColorItem } from '@/features/operations/types';

type ModelColor = AdminPhoneModelItem['colors'][number];

/// Another model whose colours can be copied, as the shortcut lists it.
export type ColorSource = { id: string; label: string; colorIds: string[] };

/**
 * The colours a model comes in, picked from the general list (#186).
 *
 * Only active colours are offered. One the model already has stays selectable even if it was
 * deactivated since, flagged, because the model keeps it until somebody removes it -- and the
 * backend accepts keeping it while refusing to add it anew.
 *
 * "Copiar colores de…" replaces the selection with the colours of another model, since iPhone
 * repeats most colours across models: a new model starts from the previous one and is adjusted.
 * Nothing is saved until the model is saved.
 */
export function ModelColorsField({
  label,
  colors,
  value,
  onChange,
  currentColors = [],
  sources,
  disabled = false,
}: {
  label: string;
  /// The general list, active and inactive.
  colors: ColorItem[];
  value: string[];
  onChange: (colorIds: string[]) => void;
  /// What the model has today, as saved. Empty for a model being created.
  currentColors?: ModelColor[];
  sources: ColorSource[];
  disabled?: boolean;
}) {
  const [copyFrom, setCopyFrom] = useState('');
  const currentIds = currentColors.map((color) => color.id);
  const offered = colors.filter((color) => color.isActive || currentIds.includes(color.id));
  const byId = new Map(offered.map((color) => [color.id, color]));
  const selected = value.map((id) => byId.get(id)).filter((color): color is ColorItem => !!color);

  /// Removing a colour that devices of this model have is allowed: they keep it (#186). The
  /// operator is told before saving rather than stopped.
  const removedWithDevices = currentColors.filter(
    (color) => color.deviceCount > 0 && !value.includes(color.id),
  );

  const copy = (sourceId: string) => {
    const source = sources.find((option) => option.id === sourceId);

    if (source) {
      // Only what this model can have: active colours, or ones it already has.
      onChange(source.colorIds.filter((id) => byId.has(id)));
    }

    setCopyFrom('');
  };

  return (
    <Stack spacing={1}>
      <Autocomplete
        multiple
        size="small"
        options={offered}
        value={selected}
        disabled={disabled}
        filterSelectedOptions
        isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
        getOptionLabel={(color) => (color.isActive ? color.name : `${color.name} (inactivo)`)}
        onChange={(_event, chosen) => onChange(chosen.map((color) => color.id))}
        renderTags={(chosen, getTagProps) =>
          chosen.map((color, index) => (
            <Chip
              {...getTagProps({ index })}
              key={color.id}
              size="small"
              label={color.isActive ? color.name : `${color.name} (inactivo)`}
              variant={color.isActive ? 'filled' : 'outlined'}
            />
          ))
        }
        noOptionsText="No hay más colores activos. Se cargan en Administración → Colores."
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selected.length ? '' : 'Elegí colores'}
          />
        )}
      />
      {sources.length ? (
        <TextField
          select
          size="small"
          label="Copiar colores de…"
          value={copyFrom}
          disabled={disabled}
          sx={{ maxWidth: 320 }}
          onChange={(event) => copy(event.target.value)}
        >
          {sources.map((source) => (
            <MenuItem key={source.id} value={source.id}>
              {source.label} ({source.colorIds.length})
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {removedWithDevices.map((color) => (
        <Typography key={color.id} variant="caption" color="warning.main">
          Al guardar, {color.name} deja de ofrecerse para este modelo.{' '}
          {color.deviceCount === 1
            ? 'El equipo que lo tiene lo conserva.'
            : `Los ${color.deviceCount} equipos que lo tienen lo conservan.`}
        </Typography>
      ))}
    </Stack>
  );
}
